import React from 'react';
import NumberFormat from 'react-number-format';
import { Currency, CurrencyVariant, Percentage } from 'components/core';
import { ColumnType, DashboardTable } from 'modules/dashboard/components';
import { useBusiness } from 'hooks/business/use-business';
import { usePortfolios } from 'modules/portfolio/allocation/hooks/use-portfolios';
import { useTreasureReservePositions } from 'modules/treasure-reserve-positions/hooks/use-treasure-reserve-positions';
import { useProductPositions } from 'modules/treasure-reserve-positions/hooks/use-product-positions';
import { dayjs } from 'app/dayjs';
import { FixedIncomeList } from 'modules/2025-q3/fixed-income-transactions/fixed-income-list/fixed-income-list';
import { Box } from '@material-ui/core';

export type Fund = {
  coupon_rate: string;
  cusip: string;
  dollarValue: string;
  maturityDate: string;
  name: string;
};

const fundColumns: ColumnType[] = [
  { name: 'Asset Name', size: 5 },
  { alignment: 'center', name: 'Ticker', size: 2 },
  { alignment: 'right', name: 'Market Value', size: 2 },
  { alignment: 'right', name: 'Shares', size: 2 },
  { size: 1 },
];

const uninvestedColumns: ColumnType[] = [
  { name: 'Asset Name', size: 7 },
  { alignment: 'right', name: 'Market Value', size: 2 },
  { size: 3 },
];

const otherPositionsColumns: ColumnType[] = [
  { name: 'Asset Name', size: 4 },
  { name: 'CUSIP', size: 2 },
  { alignment: 'right', name: 'Maturity Date', size: 3 },
  { alignment: 'right', name: 'Market Value', size: 3 },
];

export const otherPositionsTNoteColumns: ColumnType[] = [
  { name: 'Asset Name', size: 3 },
  { name: 'CUSIP', size: 2 },
  { alignment: 'right', name: 'Maturity Date', size: 3 },
  { alignment: 'right', name: 'Coupon Rate', size: 2 },
  { alignment: 'right', name: 'Market Value', size: 2 },
];

export enum Product {
  'Cash' = 'Cash',
  'ManagedMoneyMarket' = 'Managed Money Market',
  'ManagedTreasuries' = 'Managed Treasuries',
  'ManagedIncome' = 'Managed Income',
  'Other' = 'Other',
  'OtherPositions' = 'Other Positions',
}

interface ProductPositionsProps {
  product: Product;
}

export const ProductPositions: React.FC<ProductPositionsProps> = ({
  product,
}) => {
  const { data: business, isLoading: isLoadingBusiness } = useBusiness();
  const { data: portfolio, isLoading: isLoadingPortfolios } = usePortfolios(
    business?.Id,
  );
  const { data: positions, isLoading: isLoadingTreasureReservePositions } =
    useTreasureReservePositions(business?.Id);
  const { data: productPositions, isLoading: isLoadingProductPositions } =
    useProductPositions();

  const managedMoneyMarketCusips = Array.from(
    new Set(
      productPositions?.managedMoneyMarketAllocation?.map(
        (fund: {
          fund: {
            cusip: string;
          };
        }) => fund?.fund?.cusip,
      ),
    ),
  );

  const managedIncomeCusips = Array.from(
    new Set(
      productPositions?.managedIncomeAllocation?.map(
        (fund: {
          fund: {
            cusip: string;
          };
        }) => fund?.fund?.cusip,
      ),
    ),
  );

  const managedTreasuriesCusips = Array.from(
    new Set(
      productPositions?.managedTreasuriesAllocation?.map(
        (fund: {
          fund: {
            cusip: string;
          };
        }) => fund?.fund?.cusip,
      ),
    ),
  );

  if (
    isLoadingBusiness ||
    isLoadingPortfolios ||
    isLoadingTreasureReservePositions ||
    isLoadingProductPositions
  ) {
    return null;
  }

  if (
    product === Product.ManagedMoneyMarket ||
    product === Product.ManagedIncome ||
    product === Product.ManagedTreasuries
  ) {
    const tableData: [
      string,
      string | null,
      React.ReactElement | null,
      React.ReactElement | null,
    ][] = [];

    let funds = [];

    if (product === Product.ManagedMoneyMarket) {
      funds = positions?.filter((position: { cusip: string }) =>
        managedMoneyMarketCusips.includes(position.cusip),
      );
    }

    if (product === Product.ManagedIncome) {
      funds = positions?.filter((position: { cusip: string }) =>
        managedIncomeCusips.includes(position.cusip),
      );
    }

    if (product === Product.ManagedTreasuries) {
      funds = positions?.filter((position: { cusip: string }) =>
        managedTreasuriesCusips.includes(position.cusip),
      );
    }

    funds?.map(
      (fund: {
        cusip: string;
        name: string;
        ticker: string;
        shares: number;
        dollarValue: number;
      }) => {
        return tableData.push([
          fund.name,
          fund.ticker,
          <Currency
            number={Number(fund.dollarValue)}
            variant={CurrencyVariant.Full}
          />,
          <NumberFormat
            decimalScale={3}
            displayType="text"
            fixedDecimalScale
            thousandSeparator
            value={fund.shares}
          />,
        ]);
      },
    );

    if (tableData?.length === 0) {
      return null;
    }

    return <DashboardTable columns={fundColumns} data={tableData} />;
  }

  if (product === Product.OtherPositions) {
    let funds = [];

    funds = positions?.filter(
      (position: { cusip: string }) =>
        !managedMoneyMarketCusips.includes(position.cusip) &&
        !managedIncomeCusips.includes(position.cusip) &&
        !managedTreasuriesCusips.includes(position.cusip),
    );

    funds.sort(
      (a: { maturityDate: string }, b: { maturityDate: string }) =>
        new Date(a.maturityDate).getTime() - new Date(b.maturityDate).getTime(),
    );

    const tnotes: Fund[] = funds.filter(
      (fund: { name: string }) => fund.name === 'US T Note',
    );

    const notTNotes = funds.filter(
      (fund: { name: string }) => fund.name !== 'US T Note',
    );

    const hasTNote = tnotes.length > 0;

    const tableData: [
      string,
      string,
      string | null,
      React.ReactElement | null,
    ][] = [];

    const tableDataTNote: [
      string,
      string,
      string | null,
      React.ReactElement | null,
      React.ReactElement | null,
    ][] = [];

    if (hasTNote) {
      notTNotes?.map(
        (fund: {
          cusip: string;
          name: string;
          ticker: string;
          shares: number;
          dollarValue: number;
          coupon_rate: string;
          maturityDate: string;
        }) => {
          return tableDataTNote.push([
            fund.name,
            fund.cusip,
            fund.maturityDate
              ? dayjs(fund.maturityDate).format('MMMM D, YYYY')
              : null,
            fund.coupon_rate ? (
              <Percentage decimalScale={3} number={+fund.coupon_rate} />
            ) : null,
            <Currency
              number={fund.dollarValue}
              variant={CurrencyVariant.Full}
            />,
          ]);
        },
      );
    } else {
      notTNotes?.map(
        (fund: {
          cusip: string;
          name: string;
          ticker: string;
          shares: number;
          dollarValue: number;
          maturityDate: string;
        }) => {
          return tableData.push([
            fund.name,
            fund.cusip,
            fund.maturityDate
              ? dayjs(fund.maturityDate).format('MMMM D, YYYY')
              : null,
            <Currency
              number={fund.dollarValue}
              variant={CurrencyVariant.Full}
            />,
          ]);
        },
      );
    }

    if (hasTNote) {
      return (
        <>
          {tableDataTNote?.length === 0 ? null : (
            <Box mb={6}>
              <DashboardTable
                columns={otherPositionsTNoteColumns}
                data={tableDataTNote}
              />
            </Box>
          )}
          <FixedIncomeList funds={tnotes} />
        </>
      );
    }

    if (tableData?.length > 0) {
      return (
        <DashboardTable columns={otherPositionsColumns} data={tableData} />
      );
    }

    return null;
  }

  // TODO: if/when we allow businesses to purchase funds that aren't within our products
  // add another Other section? (currently Other is named Uninvested)
  // and list the below funds (currently we're only showing Cash from above)
  if (product === Product.Other) {
    const tableData: [string, React.ReactElement | null][] = [];

    tableData.push([
      'Cash',
      <Currency
        number={portfolio?.amountsInDollars?.productAmounts.otherCash}
        variant={CurrencyVariant.Full}
      />,
    ]);

    if (tableData?.length === 0) {
      return null;
    }

    return <DashboardTable columns={uninvestedColumns} data={tableData} />;
  }

  return null;
};
